import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	Lead,
	LeadsTableRow,
	LeadStageType,
	PaginatedResponse,
} from '@ekhodealer/ekho-common';
import type { SearchLeadsParams } from './useSearchLeads';

import { apiClient } from '@config/server/apiClient';
import { leadsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface UpdateLeadStageParams {
	leadId: string;
	servicesProviderId: string;
	stage: LeadStageType;
	stageReason: string;
	optimisticUpdateContext?: {
		sourceStage: string;
		targetStageName: string;
		currentQueryParams: SearchLeadsParams;
		isKanbanView?: boolean;
	};
}

/**
 * API function to update a lead's stage
 */
export const updateLeadStage = async ({
	leadId,
	servicesProviderId,
	stage,
	stageReason,
}: UpdateLeadStageParams): Promise<Lead> => {
	const response = await apiClient.patch<Lead>(
		leadsEndpoints.updateStage(servicesProviderId, leadId),
		{ stage, stageReason },
	);
	return response.data;
};

/**
 * Hook for updating a lead's stage with optimistic updates
 * Supports both Table view (in-place update) and Kanban view (move between queries)
 */
export const useUpdateLeadStage = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateLeadStage,
		onMutate: async (variables) => {
			const { leadId, servicesProviderId, stage, optimisticUpdateContext } = variables;

			if (!optimisticUpdateContext) {
				return undefined;
			}

			const { sourceStage, targetStageName, currentQueryParams, isKanbanView } =
				optimisticUpdateContext;
			const { filters, limit, query } = currentQueryParams;

			if (isKanbanView) {
				const getStageQueryKey = (stageId: string) =>
					queries.leads.list(servicesProviderId, {
						filters: {
							...filters,
							stage: stageId,
						},
						limit,
						cursor: undefined,
						query,
					}).queryKey;

				const sourceKey = getStageQueryKey(sourceStage);
				const targetKey = getStageQueryKey(stage);

				await queryClient.cancelQueries({ queryKey: sourceKey });
				await queryClient.cancelQueries({ queryKey: targetKey });

				const previousSourceData =
					queryClient.getQueryData<PaginatedResponse<LeadsTableRow>>(sourceKey);
				const previousTargetData =
					queryClient.getQueryData<PaginatedResponse<LeadsTableRow>>(targetKey);

				const itemToMove = previousSourceData?.data.find((item) => item.id === leadId);

				if (!itemToMove) {
					return undefined;
				}

				// Remove from source
				queryClient.setQueryData<PaginatedResponse<LeadsTableRow>>(sourceKey, (old) => {
					if (!old) return old;
					return {
						...old,
						data: old.data.filter((item) => item.id !== leadId),
						count: Math.max(0, old.count - 1),
					};
				});
				queryClient.setQueryData<PaginatedResponse<LeadsTableRow>>(targetKey, (old) => {
					if (!old) return old;
					return {
						...old,
						data: [
							{
								...itemToMove,
								stage: {
									type: stage,
									name: targetStageName,
								},
							},
							...old.data,
						],
						count: old.count + 1,
					};
				});

				return {
					previousSourceData,
					previousTargetData,
					sourceKey,
					targetKey,
				};
			} else {
				const currentQueryKey = queries.leads.list(
					servicesProviderId,
					currentQueryParams,
				).queryKey;

				await queryClient.cancelQueries({ queryKey: currentQueryKey });

				const previousData =
					queryClient.getQueryData<PaginatedResponse<LeadsTableRow>>(currentQueryKey);

				if (!previousData) {
					return undefined;
				}
				queryClient.setQueryData<PaginatedResponse<LeadsTableRow>>(
					currentQueryKey,
					(old) => {
						if (!old) return old;
						return {
							...old,
							data: old.data.map((item) =>
								item.id === leadId
									? {
											...item,
											stage: {
												type: stage,
												name: targetStageName,
											},
										}
									: item,
							),
						};
					},
				);

				return {
					previousData,
					currentQueryKey,
				};
			}
		},
		onError: (_error, _variables, context) => {
			if (context) {
				if ('sourceKey' in context && 'targetKey' in context) {
					const { previousSourceData, previousTargetData, sourceKey, targetKey } =
						context;
					if (previousSourceData) {
						queryClient.setQueryData(sourceKey, previousSourceData);
					}
					if (previousTargetData) {
						queryClient.setQueryData(targetKey, previousTargetData);
					}
				} else if ('currentQueryKey' in context) {
					const { previousData, currentQueryKey } = context;
					if (previousData) {
						queryClient.setQueryData(currentQueryKey, previousData);
					}
				}
			}

			queryClient.invalidateQueries({
				queryKey: queries.leads._def,
			});
		},
		onSuccess: (_data, variables) => {
			// Without optimistic updates, invalidate all queries to refetch
			if (!variables.optimisticUpdateContext) {
				queryClient.invalidateQueries({
					queryKey: queries.leads._def,
				});
				return;
			}

			// With optimistic updates: only invalidate stats, not list queries
			// This prevents stale search index data from overwriting the optimistic update
			queryClient.invalidateQueries({
				queryKey: queries.leads.stats(variables.servicesProviderId).queryKey,
			});
		},
	});
};
